<template>
	<v-card v-if="hasContract" elevation="0" outlined class="supplier-performance-card">
		<v-card-text class="pa-6">
			<div v-if="!showHistoryOnly" class="performance-header d-flex align-center flex-wrap mb-4">
				<div class="d-flex align-center">
					<div class="performance-header-icon mr-2">
						<v-icon color="#1E7C83" size="18">fas fa-chart-line</v-icon>
					</div>
					<h2 class="darkColor--text mb-0">Supplier Performance</h2>
				</div>
				<span v-if="isDraft" class="status-chip status-chip--draft ml-3">
					<v-icon x-small class="mr-1">fas fa-pen</v-icon>
					Draft
				</span>
			</div>

			<div v-if="isDraft" class="kpi-display">
				<div class="kpi-meta-row mb-4">
					<div class="kpi-meta-card">
						<v-icon color="#1E7C83" size="14" class="mb-1">fas fa-calendar-days</v-icon>
						<span class="meta-label">Evaluation date</span>
						<strong class="meta-value">{{ evaluationDateFormatted }}</strong>
					</div>
					<div v-if="updatedAtFormatted" class="kpi-meta-card">
						<v-icon color="#757575" size="14" class="mb-1">fas fa-clock</v-icon>
						<span class="meta-label">Last updated</span>
						<strong class="meta-value meta-value--sm">{{ updatedAtFormatted }}</strong>
					</div>
					<div v-if="averageScore" class="kpi-meta-card kpi-meta-card--average">
						<v-icon :color="averageScoreStyle.textColor" size="14" class="mb-1">fas fa-gauge-high</v-icon>
						<span class="meta-label">Average score</span>
						<strong
							class="meta-value"
							:style="{ color: averageScoreStyle.textColor }"
						>{{ averageScore }}</strong>
					</div>
				</div>

				<div class="kpi-score-grid mb-4">
					<div
						v-for="item in kpiScoreItems"
						:key="item.key"
						class="kpi-score-card"
						:style="{
							borderColor: item.style.borderColor,
							background: item.style.cardBg,
						}"
					>
						<div
							class="kpi-score-icon"
							:style="{ backgroundColor: item.style.iconBg }"
						>
							<v-icon :color="item.style.textColor" size="14">{{ item.icon }}</v-icon>
						</div>
						<span class="kpi-score-label">{{ item.label }}</span>
						<template v-if="item.value !== null && item.value !== undefined && item.value !== ''">
							<div
								class="kpi-score-number"
								:style="{ color: item.style.textColor }"
							>{{ item.value }}</div>
							<span
								class="kpi-score-rating"
								:style="{
									backgroundColor: item.style.pillBg,
									color: item.style.textColor,
								}"
							>{{ item.rating }}</span>
						</template>
						<span v-else class="kpi-score-empty">Not set</span>
					</div>
				</div>

				<div v-if="kpiEvaluation.comment" class="kpi-comment-box mb-4">
					<div class="d-flex align-start">
						<div class="kpi-comment-icon mr-2">
							<v-icon color="#1E7C83" size="14">fas fa-comment-dots</v-icon>
						</div>
						<div>
							<p class="meta-label mb-1">Comment</p>
							<p class="kpi-comment-text mb-0">{{ kpiEvaluation.comment }}</p>
						</div>
					</div>
				</div>

				<div v-if="isDraft && canEnterKpi" class="d-flex justify-center">
					<v-btn
						color="#1E7C83"
						dark
						depressed
						class="action-btn px-8"
						@click="$emit('edit-kpi')"
					>
						<v-icon left small>fas fa-pen-to-square</v-icon>
						Continue draft
					</v-btn>
				</div>
			</div>

			<template v-if="kpiHistory.length">
				<div
					class="history-header d-flex align-center mb-6"
					:class="{ 'mt-5': isDraft }"
				>
					<h2 class="darkColor--text mb-0">Performance history</h2>
				</div>

				<div
					class="performance-history"
					:class="{ 'performance-history--primary': showHistoryOnly }"
				>
				<div class="history-legend mb-5">
					<p class="history-legend-text mb-0">
						<span class="legend-item">
							<span class="legend-dot legend-dot--green"></span>
							Green &gt; {{ KPI_TIER_GREEN_MIN }}
						</span>
						<span class="legend-separator">·</span>
						<span class="legend-item">
							<span class="legend-dot legend-dot--yellow"></span>
							Yellow {{ KPI_TIER_RED_MAX }}-{{ KPI_TIER_GREEN_MIN }}
						</span>
						<span class="legend-separator">·</span>
						<span class="legend-item">
							<span class="legend-dot legend-dot--red"></span>
							Red &lt; {{ KPI_TIER_RED_MAX }}
						</span>
					</p>
				</div>

				<div class="history-table-block">
					<div class="history-table-wrap">
						<div class="history-row history-row--head">
							<span class="history-head-cell history-head-cell--date">Date</span>
							<span class="history-head-cell">
								<v-icon x-small color="#1E7C83" class="mr-1">fas fa-truck</v-icon>
								Delivery
							</span>
							<span class="history-head-cell">
								<v-icon x-small color="#1E7C83" class="mr-1">fas fa-star</v-icon>
								Quality
							</span>
							<span class="history-head-cell">
								<v-icon x-small color="#1E7C83" class="mr-1">fas fa-headset</v-icon>
								Support
							</span>
							<span class="history-head-cell">Overall</span>
						</div>
						<div
							v-for="row in paginatedHistoryRows"
							:key="row.id"
							class="history-row history-row--data"
							:class="{ 'history-row--selected': selectedHistoryRowId === row.id }"
							role="button"
							tabindex="0"
							@click="openHistoryComment(row)"
							@keydown.enter.prevent="openHistoryComment(row)"
							@keydown.space.prevent="openHistoryComment(row)"
						>
							<div class="history-date-cell" data-label="Date">
								<v-icon color="#1E7C83" size="12" class="mr-1">fas fa-calendar-day</v-icon>
								<span>{{ row.dateFormatted }}</span>
							</div>
							<div class="history-score-cell-wrap" data-label="Delivery">
								<span
									v-if="getHistoryScoreStyle(row.delivery)"
									class="average-badge"
									:style="getHistoryScoreStyle(row.delivery)"
								>{{ row.deliveryFormatted }}</span>
								<span v-else class="history-score-plain">{{ row.deliveryFormatted }}</span>
							</div>
							<div class="history-score-cell-wrap" data-label="Quality">
								<span
									v-if="getHistoryScoreStyle(row.quality)"
									class="average-badge"
									:style="getHistoryScoreStyle(row.quality)"
								>{{ row.qualityFormatted }}</span>
								<span v-else class="history-score-plain">{{ row.qualityFormatted }}</span>
							</div>
							<div class="history-score-cell-wrap" data-label="Support">
								<span
									v-if="getHistoryScoreStyle(row.support)"
									class="average-badge"
									:style="getHistoryScoreStyle(row.support)"
								>{{ row.supportFormatted }}</span>
								<span v-else class="history-score-plain">{{ row.supportFormatted }}</span>
							</div>
							<div class="history-average" data-label="Overall">
								<span
									class="average-badge"
									:style="{
										backgroundColor: row.averageStyle.pillBg,
										color: row.averageStyle.textColor,
										borderColor: row.averageStyle.borderColor,
									}"
								>{{ row.averageFormatted }}</span>
							</div>
						</div>
					</div>
					<v-data-footer
						v-if="historyRows.length > 0"
						class="history-data-footer"
						:options.sync="historyFooterOptions"
						:pagination="historyFooterPagination"
						:items-per-page-options="[5, 10, 15]"
						:show-current-page="false"
						items-per-page-text="Rows per page:"
					>
						<template #page-text="{ pageStart, pageStop, itemsLength }">
							{{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
						</template>
					</v-data-footer>
				</div>

				<supplier-kpi-history-chart :history="kpiHistory" />
				</div>
			</template>

			<evaluation-comment-dialog
				v-model="showCommentDialog"
				:comment="selectedHistoryComment"
			/>

			<div v-if="showEmptyState" class="supplier-performance-empty text-center">
				<div class="empty-icon mx-auto mb-4">
					<v-icon color="#1E7C83" size="36">fas fa-chart-column</v-icon>
				</div>
				<p class="empty-title mb-2">No KPI evaluation yet</p>
				<p class="grey--text text--darken-1 mb-0 empty-subtitle">
					Use <strong>KPI evaluation</strong> in Contract Status to get started.
				</p>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import SupplierKpiHistoryChart from "./SupplierKpiHistoryChart.vue"
import EvaluationCommentDialog from "./EvaluationCommentDialog.vue"
import {
	calculateKpiMean,
	getOverallPerformanceStyle,
	KPI_TIER_GREEN_MIN,
	KPI_TIER_RED_MAX,
} from "../utils/supplierKpiOverall"

const KPI_RATINGS = {
	5: "Excellent",
	4: "Good",
	3: "Average",
	2: "Poor",
	1: "Very Poor",
}

const KPI_STATUS = {
	draft: 1,
	saved: 2,
	shared: 3,
}

const KPI_ICONS = {
	deliveryKpi: "fas fa-truck",
	qualityKpi: "fas fa-star",
	supportKpi: "fas fa-headset",
}

const KPI_SCORE_STYLES = {
	5: {
		textColor: "#2e7d32",
		borderColor: "#a5d6a7",
		cardBg: "#f3faf4",
		iconBg: "#c8e6c9",
		pillBg: "#e8f5e9",
	},
	4: {
		textColor: "#1E7C83",
		borderColor: "#80cbc4",
		cardBg: "#f0faf9",
		iconBg: "#b2dfdb",
		pillBg: "#e8f4f3",
	},
	3: {
		textColor: "#f57f17",
		borderColor: "#ffe082",
		cardBg: "#fffdf5",
		iconBg: "#fff9c4",
		pillBg: "#fff8e1",
	},
	2: {
		textColor: "#ef6c00",
		borderColor: "#ffcc80",
		cardBg: "#fff8f0",
		iconBg: "#ffe0b2",
		pillBg: "#fff3e0",
	},
	1: {
		textColor: "#c62828",
		borderColor: "#ef9a9a",
		cardBg: "#fff5f5",
		iconBg: "#ffcdd2",
		pillBg: "#ffebee",
	},
}

const KPI_DEFAULT_STYLE = {
	textColor: "#757575",
	borderColor: "#e0e0e0",
	cardBg: "#fafafa",
	iconBg: "#eeeeee",
	pillBg: "#f5f5f5",
}

export default {
	name: "SupplierPerformance",
	components: {
		SupplierKpiHistoryChart,
		EvaluationCommentDialog,
	},
	props: {
		hasContract: {
			type: Boolean,
			default: false,
		},
		kpiEvaluation: {
			type: Object,
			default: null,
		},
		kpiHistory: {
			type: Array,
			default: () => [],
		},
		canEnterKpi: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			KPI_TIER_GREEN_MIN,
			KPI_TIER_RED_MAX,
			showCommentDialog: false,
			selectedHistoryRowId: null,
			selectedHistoryComment: "",
			historyFooterOptions: {
				page: 1,
				itemsPerPage: 5,
			},
		}
	},
	computed: {
		isDraft() {
			return this.kpiEvaluation?.status === KPI_STATUS.draft
		},
		showHistoryOnly() {
			return this.kpiHistory.length > 0 && !this.isDraft
		},
		showEmptyState() {
			return !this.isDraft && this.kpiHistory.length === 0
		},
		evaluationDateFormatted() {
			if (!this.kpiEvaluation?.evaluationDate) {
				return "—"
			}
			return this.$moment(this.kpiEvaluation.evaluationDate).format("D MMM YYYY")
		},
		updatedAtFormatted() {
			const timestamp = this.kpiEvaluation?.updatedAt || this.kpiEvaluation?.createdAt
			if (!timestamp) {
				return ""
			}
			return this.$moment(timestamp).format("D MMM YYYY, HH:mm")
		},
		kpiScoreItems() {
			if (!this.kpiEvaluation) {
				return []
			}
			return [
				this.buildScoreItem("deliveryKpi", "Delivery", this.kpiEvaluation.deliveryKpi),
				this.buildScoreItem("qualityKpi", "Quality", this.kpiEvaluation.qualityKpi),
				this.buildScoreItem("supportKpi", "Support", this.kpiEvaluation.supportKpi),
			]
		},
		averageScore() {
			if (!this.kpiEvaluation) {
				return null
			}
			const mean = calculateKpiMean(
				this.kpiEvaluation.deliveryKpi,
				this.kpiEvaluation.qualityKpi,
				this.kpiEvaluation.supportKpi,
			)
			return mean !== null ? mean.toFixed(1) : null
		},
		averageScoreStyle() {
			if (!this.kpiEvaluation) {
				return KPI_DEFAULT_STYLE
			}
			return getOverallPerformanceStyle(
				this.kpiEvaluation.deliveryKpi,
				this.kpiEvaluation.qualityKpi,
				this.kpiEvaluation.supportKpi,
			)
		},
		historyRows() {
			return this.kpiHistory.map((entry) => this.buildHistoryRow(entry))
		},
		historyFooterPagination() {
			const itemsLength = this.historyRows.length
			const itemsPerPage = this.historyFooterOptions.itemsPerPage
			const page = this.historyFooterOptions.page
			const pageCount = itemsLength === 0 ? 1 : Math.ceil(itemsLength / itemsPerPage)
			return {
				page,
				itemsPerPage,
				pageStart: itemsLength === 0 ? 0 : (page - 1) * itemsPerPage,
				pageStop: itemsLength === 0 ? 0 : Math.min(page * itemsPerPage, itemsLength),
				pageCount,
				itemsLength,
			}
		},
		paginatedHistoryRows() {
			const { page, itemsPerPage } = this.historyFooterOptions
			const start = (page - 1) * itemsPerPage
			return this.historyRows.slice(start, start + itemsPerPage)
		},
	},
	watch: {
		showCommentDialog(value) {
			if (!value) {
				this.selectedHistoryRowId = null
			}
		},
		"historyRows.length"(length) {
			const pageCount = Math.max(1, Math.ceil(length / this.historyFooterOptions.itemsPerPage))
			if (this.historyFooterOptions.page > pageCount) {
				this.historyFooterOptions = {
					...this.historyFooterOptions,
					page: pageCount,
				}
			}
		},
	},
	methods: {
		openHistoryComment(row) {
			this.selectedHistoryRowId = row.id
			this.selectedHistoryComment = row.comment || ""
			this.showCommentDialog = true
		},
		buildHistoryRow(entry) {
			const delivery = entry.deliveryKpi
			const quality = entry.qualityKpi
			const support = entry.supportKpi
			const average = calculateKpiMean(delivery, quality, support)
			return {
				id: entry.id,
				comment: entry.comment || "",
				dateFormatted: entry.evaluationDate
					? this.$moment(entry.evaluationDate).format("D MMM YYYY")
					: "—",
				delivery: delivery,
				quality: quality,
				support: support,
				deliveryFormatted: this.formatScore(delivery),
				qualityFormatted: this.formatScore(quality),
				supportFormatted: this.formatScore(support),
				averageFormatted: average !== null ? average.toFixed(1) : "—",
				averageStyle: getOverallPerformanceStyle(delivery, quality, support),
			}
		},
		getHistoryScoreStyle(value) {
			const parsed = value === null || value === undefined || value === ""
				? null
				: parseInt(value, 10)
			if (parsed === 1) {
				return {
					color: KPI_SCORE_STYLES[1].textColor,
					backgroundColor: KPI_SCORE_STYLES[1].pillBg,
					borderColor: KPI_SCORE_STYLES[1].borderColor,
				}
			}
			if (parsed === 5) {
				return {
					color: KPI_SCORE_STYLES[5].textColor,
					backgroundColor: KPI_SCORE_STYLES[5].pillBg,
					borderColor: KPI_SCORE_STYLES[5].borderColor,
				}
			}
			return null
		},
		formatScore(value) {
			if (value === null || value === undefined || value === "") {
				return "—"
			}
			return parseInt(value, 10).toFixed(1)
		},
		buildScoreItem(key, label, value) {
			const parsed = value === null || value === undefined || value === ""
				? null
				: parseInt(value, 10)
			return {
				key,
				label,
				icon: KPI_ICONS[key] || "fas fa-chart-simple",
				value: parsed,
				rating: parsed ? KPI_RATINGS[parsed] || "" : "",
				style: parsed ? KPI_SCORE_STYLES[parsed] || KPI_DEFAULT_STYLE : KPI_DEFAULT_STYLE,
			}
		},
	},
}
</script>

<style scoped>
.supplier-performance-card {
	border-radius: 10px;
	background: #fff;
}

.performance-header-icon {
	width: 36px;
	height: 36px;
	border-radius: 10px;
	background: #e8f4f3;
	display: flex;
	align-items: center;
	justify-content: center;
}

.status-chip {
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 700;
	border: 1px solid transparent;
}

.status-chip--draft {
	background: #fff8e1;
	color: #f57f17;
	border-color: #ffe082;
}

.status-chip--saved {
	background: #e8f5e9;
	color: #2e7d32;
	border-color: #c8e6c9;
}

.status-chip--shared {
	background: #e8f4f3;
	color: #1E7C83;
	border-color: #b2dfdb;
}

.kpi-meta-row {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 10px;
	max-width: 640px;
	margin: 0 auto;
}

.kpi-meta-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 12px 10px;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.kpi-meta-card--average {
	border-width: 1.5px;
}

.meta-label {
	display: block;
	color: #757575;
	font-size: 0.625rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 2px;
}

.meta-value {
	font-size: 0.875rem;
	font-weight: 700;
	color: #1a1a1a;
	line-height: 1.2;
}

.meta-value--sm {
	font-size: 0.75rem;
}

.kpi-score-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 10px;
	max-width: 640px;
	margin: 0 auto;
}

.kpi-score-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 12px 8px 10px;
	border: 1.5px solid #e8e8e8;
	border-radius: 8px;
}

.kpi-score-icon {
	width: 28px;
	height: 28px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 6px;
}

.kpi-score-label {
	color: #616161;
	font-size: 0.6875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 4px;
}

.kpi-score-number {
	font-size: 1.375rem;
	font-weight: 800;
	line-height: 1;
	margin-bottom: 4px;
}

.kpi-score-rating {
	display: inline-block;
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 0.6875rem;
	font-weight: 700;
}

.kpi-score-empty {
	color: #9e9e9e;
	font-size: 0.875rem;
	font-style: italic;
}

.kpi-comment-box {
	background: linear-gradient(135deg, #f8fcfb 0%, #f3f9f8 100%);
	border: 1px solid #d7ece9;
	border-left: 3px solid #1E7C83;
	border-radius: 8px;
	padding: 12px 14px;
	max-width: 640px;
	margin: 0 auto;
}

.kpi-comment-icon {
	width: 26px;
	height: 26px;
	border-radius: 6px;
	background: #e8f4f3;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.kpi-comment-text {
	color: #424242;
	font-size: 0.8125rem;
	line-height: 1.45;
	white-space: pre-wrap;
}

.supplier-performance-empty {
	padding: 24px 16px 12px;
}

.empty-icon {
	width: 72px;
	height: 72px;
	border-radius: 50%;
	background: #e8f4f3;
	display: flex;
	align-items: center;
	justify-content: center;
}

.empty-title {
	font-weight: 700;
	color: #1a1a1a;
	font-size: 1.05rem;
}

.empty-subtitle {
	max-width: 360px;
	margin-left: auto;
	margin-right: auto;
	line-height: 1.5;
}

.action-btn {
	text-transform: none;
	font-weight: 600;
	letter-spacing: 0.02em;
	border-radius: 8px;
}

.performance-history {
	width: 100%;
}

.history-header {
	width: 100%;
	justify-content: flex-start;
}

.history-header h2 {
	font-size: 1.5rem;
	font-weight: 700;
}

.history-legend {
	text-align: center;
}

.history-legend-text {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	color: #616161;
	font-size: 0.875rem;
	line-height: 1.6;
}

.legend-item {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
}

.legend-separator {
	margin: 0 8px;
	color: #bdbdbd;
}

.legend-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	margin-right: 6px;
	flex-shrink: 0;
}

.legend-dot--green {
	background: #2e7d32;
}

.legend-dot--yellow {
	background: #f9a825;
}

.legend-dot--red {
	background: #c62828;
}

.history-table-block {
	width: 100%;
}

.history-table-wrap {
	width: 100%;
	border: 1px solid #e8e8e8;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
	background: #fff;
}

.history-data-footer {
	border: none;
	border-radius: 0;
	box-shadow: none;
	background: transparent;
	padding: 0 4px;
}

.history-data-footer >>> .v-data-footer__select .v-input {
	max-width: 56px;
	margin-top: 0;
	padding-top: 0;
}

.history-data-footer >>> .v-data-footer__select .v-input__slot {
	min-height: 32px !important;
}

.history-data-footer >>> .v-data-footer__select .v-select__selections {
	justify-content: center;
}

.history-data-footer >>> .v-data-footer__pagination {
	margin-right: 16px;
}

.history-table-wrap .history-row--head {
	border-radius: 0;
	border: none;
	border-bottom: 2px solid #dceee9;
}

.history-table-wrap .history-row--data {
	border-radius: 0;
	border: none;
	border-bottom: 1px solid #f0f0f0;
}

.history-table-wrap .history-row--data:last-child {
	border-bottom: none;
}

.history-row {
	display: grid;
	grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
	align-items: center;
	gap: 10px;
	padding: 14px 18px;
	font-size: 0.875rem;
	color: #424242;
}

.history-row--head {
	background: linear-gradient(180deg, #f7fbfb 0%, #f0f7f6 100%);
	font-weight: 700;
	color: #1E7C83;
	font-size: 0.8125rem;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	padding: 12px 18px;
}

.history-row--head span {
	text-align: center;
}

.history-head-cell {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.history-head-cell--date {
	justify-content: flex-start;
}

.history-row--head .history-head-cell:first-child {
	text-align: left;
}

.history-row--data {
	background: #fff;
	transition: background-color 0.15s ease, box-shadow 0.15s ease;
	cursor: pointer;
}

.history-row--data:hover {
	background: #fafcfc;
}

.history-row--selected {
	box-shadow: inset 0 0 0 2px #1E7C83;
	background: #f7fbfb;
}

.history-row--selected:hover {
	background: #f7fbfb;
}

.history-date-cell {
	display: flex;
	align-items: center;
	font-weight: 600;
	color: #1a1a1a;
}

.history-score-cell-wrap {
	display: flex;
	justify-content: center;
	align-items: center;
}

.history-score-plain {
	font-weight: 600;
	color: #424242;
	font-size: 0.875rem;
}

.history-average {
	display: flex;
	justify-content: center;
}

.average-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 52px;
	padding: 6px 14px;
	border-radius: 8px;
	border: 1.5px solid;
	font-weight: 800;
	font-size: 0.9375rem;
	text-align: center;
}

@media (max-width: 768px) {
	.kpi-meta-row {
		grid-template-columns: 1fr;
	}

	.kpi-score-grid {
		grid-template-columns: 1fr;
	}

	.history-legend-text {
		font-size: 0.8125rem;
		flex-direction: column;
		align-items: center;
	}

	.legend-separator {
		display: none;
	}

	.legend-item {
		margin-top: 4px;
	}

	.history-row {
		grid-template-columns: 1fr;
		gap: 10px;
		padding: 14px;
	}

	.history-row--head {
		display: none;
	}

	.history-row--data {
		border: 1px solid #eeeeee !important;
		border-radius: 10px !important;
		margin: 10px;
	}

	.history-row--data .history-date-cell,
	.history-row--data .history-score-cell-wrap,
	.history-row--data .history-average {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
	}

	.history-row--data .history-date-cell::before,
	.history-row--data .history-score-cell-wrap::before,
	.history-row--data .history-average::before {
		content: attr(data-label);
		color: #757575;
		font-size: 0.6875rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		margin-right: 12px;
		flex-shrink: 0;
	}

	.history-row--data .history-date-cell {
		padding-bottom: 8px;
		border-bottom: 1px dashed #eeeeee;
		margin-bottom: 2px;
	}

	.history-row--data .history-date-cell::before {
		display: none;
	}

	.history-table-wrap {
		background: transparent;
		border: none;
		box-shadow: none;
	}

	.history-score-cell-wrap {
		justify-content: space-between;
	}

	.history-average {
		justify-content: space-between;
		padding-top: 4px;
		border-top: 1px dashed #eeeeee;
		margin-top: 2px;
	}
}
</style>
